import React, { useRef, useState, useEffect } from 'react'
import { Package } from '../../../../enums/package.enum'
import { isMinimumPackage } from '../../../../utils/package-validator'

interface ItemActionsProps {
  itemId: string
  stageId: string
  status: string
  index: number
  totalItems: number
  userPackage: Package
  packageExpiredAt: Date | null
  readOnly?: boolean
  onEdit: (itemId: string) => void
  onDelete: (itemId: string) => Promise<void>
  onApprove: (itemId: string) => Promise<void>
  onReject: (itemId: string) => Promise<void>
  onCopy: (itemId: string) => Promise<void>
  onMoveUp: (itemId: string, stageId: string) => Promise<void>
  onMoveDown: (itemId: string, stageId: string) => Promise<void>
  onToggleVisibility: () => void
  isHidden?: boolean
}

const ItemActions: React.FC<ItemActionsProps> = ({
  itemId,
  stageId,
  status,
  index,
  totalItems,
  userPackage,
  packageExpiredAt,
  readOnly = false,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onCopy,
  onMoveUp,
  onMoveDown,
  onToggleVisibility,
  isHidden = false
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((menuRef.current != null) && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className='position-relative'>
      <div className='list-actions d-flex align-items-center justify-content-center'>
        <div ref={menuRef} className='dropdown'>
          <button
            className='tw-text-teal focus:tw-text-teal tw-px-2 tw-py-1 hover:tw-bg-dark-teal/30 hover:tw-rounded-md'
            onClick={() => setOpenMenuId(openMenuId === itemId ? null : itemId)}
            title='Opcje'
          >
            <i className='fa fa-ellipsis-v' />
          </button>
          {openMenuId === itemId && (
            <div
              className='dropdown-menu show' style={{
                position: 'absolute',
                right: 0,
                zIndex: 1000,
                minWidth: '200px'
              }}
            >
              {!readOnly && (
                <>
                  <button
                    className='dropdown-item'
                    onClick={() => {
                      onEdit(itemId)
                      setOpenMenuId(null)
                    }}
                  >
                    <i className='fa fa-edit mr-2' /> Edytuj
                  </button>
                  {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
                    <>
                      <button
                        className='dropdown-item'
                        onClick={async () => {
                          await onCopy(itemId)
                          setOpenMenuId(null)
                        }}
                      >
                        <i className='fa fa-copy mr-2' /> Kopiuj
                      </button>
                      <button
                        className='dropdown-item'
                        onClick={async () => {
                          await onMoveUp(itemId, stageId)
                          setOpenMenuId(null)
                        }}
                        disabled={index === 0}
                      >
                        <i className='fa fa-arrow-up mr-2' /> Przesuń w górę
                      </button>
                      <button
                        className='dropdown-item'
                        onClick={async () => {
                          await onMoveDown(itemId, stageId)
                          setOpenMenuId(null)
                        }}
                        disabled={index === totalItems - 1}
                      >
                        <i className='fa fa-arrow-down mr-2' /> Przesuń w dół
                      </button>

                      <button
                        className='dropdown-item'
                        onClick={() => {
                          onToggleVisibility()
                          setOpenMenuId(null)
                        }}
                      >
                        <i className={`fa fa-eye${isHidden ? '-slash' : ''} mr-2`} />
                        {isHidden ? 'Pokaż' : 'Ukryj'}
                      </button>
                    </>
                  )}
                  <button
                    className='dropdown-item text-danger'
                    onClick={async () => {
                      await onDelete(itemId)
                      setOpenMenuId(null)
                    }}
                  >
                    <i className='fa fa-trash mr-2' /> Usuń
                  </button>
                </>
              )}
              {readOnly && isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
                <>
                  <button
                    className='dropdown-item'
                    onClick={async () => {
                      await onApprove(itemId)
                      setOpenMenuId(null)
                    }}
                    disabled={status === 'approved'}
                  >
                    <i className='fa fa-check mr-2' /> Zatwierdź
                  </button>
                  <button
                    className='dropdown-item'
                    onClick={async () => {
                      await onReject(itemId)
                      setOpenMenuId(null)
                    }}
                    disabled={status === 'rejected'}
                  >
                    <i className='fa fa-times mr-2' /> Odrzuć
                  </button>
                </>)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ItemActions
